import Footer from '@/renderer/Footer';
import Header from '@/renderer/Header';
import { useData } from '@/renderer/utils/useData';
import type { DataType, SportRecord } from './dataFetcher';

export default function VerifiedRecordPage() {
  const { sports } = useData<DataType>();

  return (
    <div className="flex h-40 min-h-[stretch] flex-1 grow flex-col justify-between overflow-hidden">
      <Header title="Verified Track Record" className="py-2 md:hidden" />
      <div className="overflow-auto">
        {/* ── Hero ──────────────────────────────────────────────────────── */}
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="relative mx-auto max-w-3xl pt-12 pb-10 sm:pt-20">
            <div className="hidden sm:block">
              <h1 className="font-bold text-3xl text-zinc-900 tracking-tight sm:text-4xl dark:text-zinc-100">
                Verified Track Record
              </h1>
              <p className="mt-4 text-lg text-zinc-600 leading-7 dark:text-zinc-400">
                RAS Picks has documented every pick since 1996: over 25 years of continuous,
                unbroken records. Since 2020, every pick is independently verified by{' '}
                <a
                  href="https://betstamp.app/u/RAS"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold text-emerald-600 hover:text-emerald-500 dark:text-emerald-400"
                >
                  Betstamp
                </a>
                , the industry standard for third-party pick verification.
              </p>
            </div>
          </div>
        </div>

        {/* ── Betstamp callout ─────────────────────────────────────────── */}
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-5 dark:border-emerald-800 dark:bg-emerald-950/40">
              <div className="flex items-start gap-4">
                <div className="flex-1">
                  <p className="font-semibold text-emerald-900 text-sm dark:text-emerald-100">
                    Betstamp Independently Verified Since 2020
                  </p>
                  <p className="mt-1 text-emerald-800 text-sm leading-6 dark:text-emerald-300">
                    Every pick is time-stamped at release and publicly auditable. No picks are
                    edited or deleted after release. The record you see is the record that happened.
                  </p>
                  <a
                    href="https://betstamp.app/u/RAS"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-2 inline-block font-semibold text-emerald-700 text-sm hover:text-emerald-600 dark:text-emerald-400 dark:hover:text-emerald-300"
                  >
                    View on Betstamp →
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Sport tables ─────────────────────────────────────────────── */}
        <div className="mt-10 px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl space-y-14">
            {sports?.map((sport) => (
              <SportSection key={sport.sportId} sport={sport} />
            ))}
          </div>
        </div>

        {/* ── What is Betstamp? ────────────────────────────────────────── */}
        <div className="mt-16 px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <h2 className="font-bold text-xl text-zinc-900 tracking-tight dark:text-zinc-100">
              What is Betstamp Verification?
            </h2>
            <div className="mt-4 space-y-3 text-zinc-600 text-sm leading-7 dark:text-zinc-400">
              <p>
                Betstamp is an independent third-party platform that records picks in real time and
                makes them publicly auditable. When a pick service connects to Betstamp, every pick
                is time-stamped at the moment of release: before the game, before the line moves.
              </p>
              <p>
                Unlike self-reported records, Betstamp verification means picks cannot be
                retroactively edited, deleted, or cherry-picked. The number you see is the number
                that actually happened, exactly as it was released to subscribers.
              </p>
              <p>
                RAS Picks began Betstamp verification in 2020 and has maintained an unbroken
                verified record since. This is the gold standard for transparency in the sports pick
                industry.
              </p>
            </div>
          </div>
        </div>

        {/* ── About RAS ─────────────────────────────────────────────────── */}
        <div className="mt-12 px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <h2 className="font-bold text-xl text-zinc-900 tracking-tight dark:text-zinc-100">
              About RAS Picks
            </h2>
            <div className="mt-4 space-y-3 text-zinc-600 text-sm leading-7 dark:text-zinc-400">
              <p>
                Right Angle Sports (RAS) Picks was founded in 1996 and has released documented
                sports picks every active season since. With over 25 years of continuous operation,
                RAS is one of the longest-running independently verified pick services in the
                industry.
              </p>
              <p>
                RAS covers NFL, College Football, College Basketball, and WNBA. The service is known
                for a disciplined, closing-line-value focused approach, prioritizing picks that beat
                the closing line, which is the strongest available predictor of long-term
                profitability.
              </p>
              <p>
                Subscribers receive picks directly via the RAS platform, with Telegram and email
                notification at the time of release. Every pick is automatically recorded to
                Betstamp at the moment it goes live.
              </p>
            </div>
          </div>
        </div>

        {/* ── FAQ ───────────────────────────────────────────────────────── */}
        <div className="mt-12 px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <h2 className="font-bold text-xl text-zinc-900 tracking-tight dark:text-zinc-100">
              Frequently Asked Questions
            </h2>
            <dl className="mt-6 space-y-6 divide-y divide-zinc-900/10 dark:divide-white/10">
              {FAQ.map((item) => (
                <div key={item.q} className="pt-6 first:pt-0">
                  <dt className="font-semibold text-zinc-900 text-sm leading-6 dark:text-zinc-100">
                    {item.q}
                  </dt>
                  <dd className="mt-2 text-zinc-600 text-sm leading-7 dark:text-zinc-400">
                    {item.a}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        </div>

        <div className="mt-16" />
        <Footer />
      </div>
    </div>
  );
}

// ── SportSection ─────────────────────────────────────────────────────────────

function SportSection({ sport }: { sport: SportRecord }) {
  const { allTime, betstamp } = sport;
  const allWinRate = winRate(allTime.wins, allTime.losses);
  const bsWinRate = winRate(betstamp.wins, betstamp.losses);

  return (
    <section aria-labelledby={`sport-${sport.sportId}`}>
      <div className="mb-4 flex items-baseline justify-between">
        <h2
          id={`sport-${sport.sportId}`}
          className="font-bold text-xl text-zinc-900 tracking-tight dark:text-zinc-100"
        >
          {sport.name}
        </h2>
        <span className="text-xs text-zinc-500 dark:text-zinc-500">
          All-time: {allTime.wins}-{allTime.losses}-{allTime.pushes} &nbsp;·&nbsp;
          {allWinRate}% &nbsp;·&nbsp;
          <Units value={allTime.units} />
        </span>
      </div>

      {/* Betstamp summary bar */}
      <div className="mb-4 rounded-lg bg-zinc-100 px-4 py-2.5 text-sm dark:bg-zinc-800">
        <span className="font-medium text-zinc-700 dark:text-zinc-300">
          Betstamp verified (2020–present):
        </span>{' '}
        <span className="text-zinc-600 dark:text-zinc-400">
          {betstamp.wins}-{betstamp.losses}-{betstamp.pushes} &nbsp;·&nbsp;
          {bsWinRate}% &nbsp;·&nbsp;
          <Units value={betstamp.units} />
        </span>
      </div>

      <div className="overflow-x-auto rounded-xl border border-zinc-200 dark:border-zinc-700">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-zinc-200 border-b bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-800/60">
              <th className="px-4 py-3 text-left font-semibold text-xs text-zinc-500 uppercase tracking-wide dark:text-zinc-400">
                Season
              </th>
              <th className="px-4 py-3 text-right font-semibold text-xs text-zinc-500 uppercase tracking-wide dark:text-zinc-400">
                Record
              </th>
              <th className="px-4 py-3 text-right font-semibold text-xs text-zinc-500 uppercase tracking-wide dark:text-zinc-400">
                Win %
              </th>
              <th className="px-4 py-3 text-right font-semibold text-xs text-zinc-500 uppercase tracking-wide dark:text-zinc-400">
                Units
              </th>
              <th className="px-4 py-3 text-right font-semibold text-xs text-zinc-500 uppercase tracking-wide dark:text-zinc-400">
                Verified
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100 dark:divide-zinc-800">
            {sport.seasons.map((season) => (
              <tr
                key={season.seasonId}
                className="transition-colors hover:bg-zinc-50 dark:hover:bg-zinc-800/40"
              >
                <td className="px-4 py-3 font-medium text-zinc-900 dark:text-zinc-100">
                  {season.name}
                </td>
                <td className="px-4 py-3 text-right text-zinc-600 tabular-nums dark:text-zinc-400">
                  {season.wins}-{season.losses}-{season.pushes}
                </td>
                <td className="px-4 py-3 text-right text-zinc-600 tabular-nums dark:text-zinc-400">
                  {winRate(season.wins, season.losses)}%
                </td>
                <td className="px-4 py-3 text-right tabular-nums">
                  <Units value={season.units} />
                </td>
                <td className="px-4 py-3 text-right">
                  {season.betstampVerified ? (
                    <span className="inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 font-medium text-emerald-700 text-xs ring-1 ring-emerald-600/20 ring-inset dark:bg-emerald-950/50 dark:text-emerald-400 dark:ring-emerald-500/30">
                      Verified
                    </span>
                  ) : (
                    <span className="text-zinc-400 text-xs dark:text-zinc-600">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function winRate(wins: number, losses: number): string {
  const total = wins + losses;
  if (total === 0) {
    return '—';
  }
  return ((wins / total) * 100).toFixed(1);
}

function Units({ value }: { value: number }) {
  const rounded = Math.round(value * 10) / 10;
  const positive = rounded >= 0;
  return (
    <span
      className={
        positive ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'
      }
    >
      {positive ? '+' : ''}
      {rounded.toFixed(1)}u
    </span>
  );
}

// ── FAQ content ───────────────────────────────────────────────────────────────

const FAQ = [
  {
    q: 'Is RAS Picks independently verified?',
    a: 'Yes. Since 2020, every pick released by RAS Picks is independently tracked and verified by Betstamp, a third-party verification platform. No picks are edited or deleted after release. You can view the complete verified record at betstamp.app.',
  },
  {
    q: 'How long has RAS Picks been operating?',
    a: 'RAS Picks was founded in 1996 and has been releasing documented picks continuously since then: over 25 years of track record.',
  },
  {
    q: 'What is Betstamp verification?',
    a: 'Betstamp is an independent third-party platform that records picks in real time and makes them publicly auditable. When a pick service is Betstamp-verified, picks are time-stamped at release and cannot be retroactively altered. This is the gold standard for pick service transparency.',
  },
  {
    q: 'What sports does RAS Picks cover?',
    a: 'RAS Picks covers NFL, College Football (CFB), College Basketball (CBB), and WNBA. The service is best known for its NFL and CFB track records.',
  },
  {
    q: 'How do I subscribe to RAS Picks?',
    a: 'Subscriptions are available directly at raspicks.com. RAS offers season-long and annual packages for NFL, CFB, CBB, and WNBA. Every subscriber receives picks via the RAS platform with Telegram and email notification at the time of release.',
  },
  {
    q: "What is RAS Picks' approach to handicapping?",
    a: "RAS focuses on closing line value (CLV): picking sides and totals that consistently beat the closing line. Beating the close is widely considered the strongest predictor of long-term profitability in sports betting and is the foundation of RAS's handicapping methodology.",
  },
];
